import { Ref } from 'vue';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keeps keyboard focus inside the active modal or drawer panel and restores
 * focus to the opener when the overlay closes.
 */
export default function useFocusTrap(containerRef: Ref<HTMLElement | null>) {
    let previouslyFocused: HTMLElement | null = null;

    const focusWithoutScrolling = (element: HTMLElement) => {
        element.focus({ preventScroll: true });
    };

    const focusableElements = (): HTMLElement[] => {
        const container = containerRef.value;
        if (!container) {
            return [];
        }

        return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    };

    const focusFirst = () => {
        const container = containerRef.value;
        if (!container) {
            return;
        }

        const [first] = focusableElements();
        focusWithoutScrolling(first ?? container);
    };

    const activate = () => {
        if (typeof document !== 'undefined') {
            previouslyFocused = document.activeElement as HTMLElement | null;
        }

        focusFirst();
    };

    const release = () => {
        if (previouslyFocused) {
            focusWithoutScrolling(previouslyFocused);
        }
        previouslyFocused = null;
    };

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') {
            return;
        }

        const focusable = focusableElements();
        if (focusable.length === 0) {
            event.preventDefault();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const container = containerRef.value;

        // Focus sitting outside the subtree (body, or a node that was removed)
        // would let Tab walk onto the page behind the overlay. Pull it back to
        // the first focusable so the trap holds from any starting state.
        if (!container || !(active instanceof Node) || !container.contains(active)) {
            event.preventDefault();
            focusWithoutScrolling(first);
            return;
        }

        if (event.shiftKey && active === first) {
            event.preventDefault();
            focusWithoutScrolling(last);
            return;
        }

        if (!event.shiftKey && active === last) {
            event.preventDefault();
            focusWithoutScrolling(first);
        }
    };

    return { activate, release, focusFirst, handleKeydown };
}
